import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { Address } from 'viem';
import { api } from '@/api/client';
import { validateQuote } from '@/api/security';
import { validateApprovalTx, validateCancelTx, validateCapacityTx } from '@/api/tx';
import { DEMO_ACTIVITY, DEMO_CAPACITY, DEMO_DRAFT, EMPTY_CAPACITY, EMPTY_DRAFT, EMPTY_TOKEN } from '@/lib/preview-data';
import { isPreview } from '@/lib/config';
import type { ActivityOrder, Capacity, Holding, StateOrderDraft, TokenStats } from '@/lib/types';
import { formatUnits, units } from '@/lib/utils';
import { publicClient } from '@/lib/wallet';

type Quote = any;
type Order = any;
type Template = { name: string; targets: { symbol: string; weight: number }[] };
type CrossState = {
  draft: StateOrderDraft;
  setTarget: (symbol: string, weight: number) => void;
  setTargets: (targets: { symbol: string; weight: number }[]) => void;
  setControls: (patch: Partial<Pick<StateOrderDraft, 'slippageBps' | 'maxSurplusShareBps' | 'useCapacity'>>) => void;
  resetDraft: () => void;
  previewAddress: string | null;
  connectPreview: () => void;
  disconnectPreview: () => void;
  quote: Quote | null;
  order: Order | null;
  activity: ActivityOrder[];
  capacity: Capacity;
  token: TokenStats;
  requestQuote: (owner: string, walletClient?: any) => Promise<Quote>;
  submitOrder: (owner: string, walletClient?: any) => Promise<Order>;
  refreshOrder: (owner: string, walletClient?: any) => Promise<Order | null>;
  cancelOrder: (owner?: string, walletClient?: any, invalidateOnchain?: boolean) => Promise<void>;
  syncAccount: (owner: string, walletClient?: any) => Promise<void>;
  saveTemplate: (name: string) => void;
  applyTemplate: (name: string) => void;
  templates: Template[];
  lockCross: (amount: number, owner?: string, walletClient?: any) => Promise<void>;
  requestUnlock: (amount: number, owner?: string, walletClient?: any) => Promise<void>;
  cancelUnlock: (owner?: string, walletClient?: any) => Promise<void>;
  withdrawUnlocked: (owner?: string, walletClient?: any) => Promise<void>;
  busy: boolean;
};
const Ctx = createContext<CrossState | null>(null);

const previewQuote = (owner: string, draft: StateOrderDraft) => ({
  id: `0x${'7f'.repeat(32)}`,
  order: {
    owner,
    receiver: owner,
    nonce: '1',
    quotedAt: Math.floor(Date.now() / 1000),
    deadline: Math.floor(Date.now() / 1000) + 120,
    maxSurplusShareBps: draft.maxSurplusShareBps,
    useCapacity: draft.useCapacity,
    legs: draft.targets.filter(t => t.weight > 0).map(t => ({ symbol: t.symbol, weightBps: Math.round(t.weight * 100) })),
  },
  typedData: {
    domain: {
      name: 'CROSS Portfolio Settlement',
      version: '3',
      chainId: 4663,
      verifyingContract: '0x0000000000000000000000000000000000000009',
    },
    message: {},
  },
  metrics: {
    internalPct: 84.7,
    residualPct: 15.3,
    savingsUsd: 20.24,
    directCostUsd: 10002.55,
    crossCostUsd: 9982.31,
    protocolTakeUsd: draft.useCapacity ? 0 : 3.5,
  },
});

async function sendChecked(walletClient: any, owner: string, tx: { to: string; data: string; value?: bigint | string }) {
  const hash = await walletClient.sendTransaction({
    account: owner as Address,
    to: tx.to as Address,
    data: tx.data as `0x${string}`,
    value: BigInt(tx.value ?? 0),
  });
  await publicClient.waitForTransactionReceipt({ hash });
  return hash;
}

const orderStatus = (value: string): ActivityOrder['status'] => {
  if (value === 'settled') return 'completed';
  if (value === 'ready') return 'cleared';
  if (['signed', 'submitted'].includes(value)) return 'routed';
  if (['reverted', 'expired'].includes(value)) return 'failed';
  if (value === 'cancelled') return 'cancelled';
  return 'in-progress';
};

const usd6 = (value?: string | number | bigint | null) => Number(formatUnits(String(value ?? '0'), 6, 6));
const cross18 = (value?: string | number | bigint | null) => Number(formatUnits(String(value ?? '0'), 18, 6));

export function CrossProvider({ children }: { children: React.ReactNode }) {
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [previewAddress, setPreviewAddress] = useState<string | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [busy, setBusy] = useState(false);
  const [activity, setActivity] = useState<ActivityOrder[]>([]);
  const [capacity, setCapacity] = useState<Capacity>(EMPTY_CAPACITY);
  const [token] = useState<TokenStats>(EMPTY_TOKEN);
  const [templates, setTemplates] = useState<Template[]>([]);

  const setTarget = useCallback((symbol: string, weight: number) => {
    const safe = Math.max(0, Math.min(100, Number.isFinite(weight) ? weight : 0));
    setDraft(d => {
      const found = d.targets.some(target => target.symbol === symbol);
      const next = found
        ? d.targets.map(target => target.symbol === symbol ? { ...target, weight: safe, value: d.totalValue * safe / 100 } : target)
        : [...d.targets, { symbol, weight: safe, value: d.totalValue * safe / 100 }];
      return { ...d, targets: next };
    });
  }, []);

  const setTargets = useCallback((next: { symbol: string; weight: number }[]) => {
    const map = new Map(next.map(x => [x.symbol, Math.max(0, Math.min(100, x.weight))]));
    setDraft(d => ({
      ...d,
      targets: d.targets.map(t => {
        const weight = map.get(t.symbol) ?? 0;
        return { ...t, weight, value: d.totalValue * weight / 100 };
      }),
    }));
  }, []);

  const setControls = useCallback((patch: Partial<Pick<StateOrderDraft, 'slippageBps' | 'maxSurplusShareBps' | 'useCapacity'>>) => {
    setDraft(d => ({ ...d, ...patch }));
  }, []);

  const authed = useCallback(async (owner: string, walletClient?: any) => {
    if (isPreview() || api.token) return;
    if (!walletClient) throw new Error('Wallet client unavailable');
    const challenge = await api.call<any>('/v3/auth/challenge', { method: 'POST', body: { owner } });
    const signature = await walletClient.signMessage({ account: owner as Address, message: challenge.message });
    const session = await api.call<any>('/v3/auth/verify', { method: 'POST', body: { id: challenge.id, signature } });
    api.token = session.token;
  }, []);

  const syncAccount = useCallback(async (owner: string, walletClient?: any) => {
    if (isPreview()) return;
    setBusy(true);
    try {
      await authed(owner, walletClient);
      const [profile, history] = await Promise.all([
        api.call<any>('/v3/me'),
        api.call<any>('/v3/orders'),
      ]);
      const prices = profile.snapshot?.prices ?? {};
      const holdings: Holding[] = (profile.holdings ?? []).filter((h: any) => h.symbol !== 'CROSS').map((h: any) => {
        const rawAmount = String(h.amount ?? '0');
        const amount = Number(formatUnits(rawAmount, Number(h.decimals), 8));
        const price = usd6(prices[h.address] ?? prices[String(h.address).toLowerCase()] ?? 0);
        const value = amount * price;
        return { symbol: h.symbol, name: h.symbol, address: h.address, decimals: Number(h.decimals), price, icon: h.symbol.slice(0, 1), amount, rawAmount, value, allocation: 0 };
      });
      const totalValue = holdings.reduce((n, h) => n + h.value, 0);
      for (const h of holdings) h.allocation = totalValue > 0 ? h.value / totalValue * 100 : 0;
      setDraft(d => ({
        ...d,
        totalValue,
        holdings,
        targets: holdings.map(holding => {
          const existing = d.targets.find(target => target.symbol === holding.symbol);
          const weight = existing?.weight ?? holding.allocation;
          return { symbol: holding.symbol, weight, value: totalValue * weight / 100 };
        }),
      }));
      const c = profile.capacity ?? {};
      setCapacity({
        locked: cross18(c.locked),
        daily: usd6(c.quota),
        used: usd6(c.used),
        remaining: usd6(c.remaining),
        exitAmount: cross18(c.exitAmount),
        exitReadyAt: Number(c.exitReadyAt) > 0 ? new Date(Number(c.exitReadyAt) * 1000).toISOString() : undefined,
      });
      const mapped: ActivityOrder[] = (history.orders ?? []).map((o: any) => {
        const internal = Number(o.batch?.metrics?.internalizedBps ?? 0) / 100;
        const total = usd6(o.quote?.totalValueUsd);
        const receipt = o.receipt ?? {};
        const savings = usd6(receipt.userSurplusUsd ?? o.batch?.metrics?.userSurplusUsd);
        const take = usd6(receipt.feeValueUsd ?? o.batch?.metrics?.protocolFeeUsd);
        return {
          id: String(o.id),
          time: new Date(Number(o.createdAt ?? Date.now() / 1000) * (Number(o.createdAt) < 1e12 ? 1000 : 1)).toLocaleString(),
          shift: 'Portfolio State Order',
          value: total,
          status: orderStatus(String(o.status)),
          internal,
          residual: Math.max(0, 100 - internal),
          savings,
          take,
          burn: 0,
        };
      });
      setActivity(mapped);
    } catch (e: any) {
      toast.error(e.message || 'Unable to refresh CROSS account');
      throw e;
    } finally {
      setBusy(false);
    }
  }, [authed]);

  const requestQuote = useCallback(async (owner: string, walletClient?: any) => {
    setBusy(true);
    try {
      if (isPreview()) {
        const next = previewQuote(owner, draft);
        setQuote(next);
        return next;
      }
      await authed(owner, walletClient);
      const meta = await api.call<any>('/v3/meta');
      const request = {
        owner,
        portfolio: draft.holdings.filter(h => h.amount > 0 || (h.rawAmount && BigInt(h.rawAmount) > 0n)).map(h => ({
          token: h.address,
          amount: h.rawAmount ?? units(String(h.amount), h.decimals),
        })),
        targets: draft.targets.filter(t => t.weight > 0).map(t => {
          const holding = draft.holdings.find(h => h.symbol === t.symbol);
          if (!holding?.address) throw new Error(`No canonical token address for ${t.symbol}`);
          return { token: holding.address, weightBps: Math.round(t.weight * 100) };
        }),
        slippageBps: draft.slippageBps,
        maxSurplusShareBps: draft.maxSurplusShareBps,
        useCapacity: draft.useCapacity,
      };
      const next = validateQuote(await api.call('/v3/quotes', { method: 'POST', body: request }), request, meta);
      setQuote(next);
      return next;
    } catch (e: any) {
      toast.error(e.message);
      throw e;
    } finally {
      setBusy(false);
    }
  }, [draft, authed]);

  const submitOrder = useCallback(async (owner: string, walletClient?: any) => {
    if (!quote) throw new Error('Request a quote first');
    setBusy(true);
    try {
      if (isPreview()) {
        const turnover = draft.targets.reduce((total, target) => {
          const current = draft.holdings.find(holding => holding.symbol === target.symbol)?.allocation ?? 0;
          return total + Math.abs(target.weight - current);
        }, 0) / 2 * draft.totalValue / 100;
        const now = Math.floor(Date.now() / 1000);
        const next = { id: 'DEMO-SO-7F3A-9C2D', status: 'settled', createdAt: now, updatedAt: now, receipt: { turnoverUsd: String(Math.round(turnover * 1_000_000)), userSurplusUsd: '20240000', feeValueUsd: '0' } };
        setOrder(next);
        setActivity(a => [{ id: next.id, time: new Date().toLocaleString(), shift: 'Portfolio rebalance', value: turnover, status: 'completed', internal: 84.7, residual: 15.3, savings: 20.24, take: 0, burn: 0 }, ...a]);
        return next;
      }
      if (!walletClient) throw new Error('Wallet client unavailable');
      await authed(owner, walletClient);
      const meta = await api.call<any>('/v3/meta');
      for (const leg of (quote.order?.legs || []).filter((l: any) => BigInt(l.sellAmount) > 0n)) {
        let tx = await api.call<any>('/v3/approvals', { method: 'POST', body: { quoteId: quote.id, token: leg.token } });
        const amount = tx.resetRequired ? 0n : BigInt(leg.sellAmount);
        await sendChecked(walletClient, owner, validateApprovalTx(tx, leg.token as Address, meta.settlement as Address, amount));
        if (tx.resetRequired) {
          tx = await api.call<any>('/v3/approvals', { method: 'POST', body: { quoteId: quote.id, token: leg.token } });
          await sendChecked(walletClient, owner, validateApprovalTx(tx, leg.token as Address, meta.settlement as Address, BigInt(leg.sellAmount)));
        }
      }
      if (Math.floor(Date.now() / 1000) >= Number(quote.order.deadline)) throw new Error('Quote expired during approvals. Request a new quote before signing.');
      const signature = await walletClient.signTypedData({ account: owner as Address, ...quote.typedData });
      const next = await api.call<any>('/v3/orders', { method: 'POST', body: { quoteId: quote.id, signature } });
      setOrder(next);
      return next;
    } catch (e: any) {
      toast.error(e.message);
      throw e;
    } finally {
      setBusy(false);
    }
  }, [quote, draft.totalValue, authed]);

  const refreshOrder = useCallback(async (owner: string, walletClient?: any) => {
    if (!order || isPreview()) return order;
    await authed(owner, walletClient);
    const next = await api.call<any>(`/v3/orders/${order.id}`);
    setOrder(next);
    return next;
  }, [order, authed]);

  const cancelOrder = useCallback(async (owner?: string, walletClient?: any, invalidateOnchain = true) => {
    if (!order) return;
    if (isPreview()) {
      setOrder({ ...order, status: 'cancelled' });
      toast.success('Order cancelled');
      return;
    }
    if (!owner || !walletClient) throw new Error('Connect your wallet');
    await authed(owner, walletClient);
    const meta = await api.call<any>('/v3/meta');
    const result = await api.call<any>(`/v3/orders/${order.id}/cancel`, { method: 'POST', body: {} });
    if (invalidateOnchain && result.transaction) {
      await sendChecked(walletClient, owner, validateCancelTx(result.transaction, meta.settlement as Address, BigInt(order.order.nonce)));
    }
    setOrder(result.order ?? { ...order, status: 'cancelled' });
    toast.success('Cancellation recorded');
  }, [order, authed]);

  const capacityAction = useCallback(async (
    action: 'lock' | 'requestUnlock' | 'cancelUnlock' | 'withdrawUnlocked',
    amount: number | undefined,
    owner?: string,
    walletClient?: any,
  ) => {
    if (!owner || !walletClient) throw new Error('Connect your wallet');
    await authed(owner, walletClient);
    const meta = await api.call<any>('/v3/meta');
    const raw = amount == null ? undefined : units(String(amount), 18);
    if (action === 'lock') {
      const approval = await api.call<any>('/v3/capacity/transaction', { method: 'POST', body: { action: 'approve', amount: raw } });
      await sendChecked(walletClient, owner, validateApprovalTx(approval, meta.crossToken as Address, meta.credits as Address, BigInt(raw!)));
    }
    const apiAction = action === 'withdrawUnlocked' ? 'withdraw' : action;
    const tx = await api.call<any>('/v3/capacity/transaction', { method: 'POST', body: { action: apiAction, amount: raw } });
    await sendChecked(walletClient, owner, validateCapacityTx(tx, meta.credits as Address, action, raw == null ? undefined : BigInt(raw)));
    await syncAccount(owner, walletClient);
  }, [authed, syncAccount]);

  const lockCross = useCallback(async (amount: number, owner?: string, walletClient?: any) => {
    if (!(amount > 0)) throw new Error('Enter a positive CROSS amount');
    setBusy(true);
    try {
      if (isPreview()) {
        setCapacity(c => ({ ...c, locked: c.locked + amount, daily: c.daily + amount * 0.5, remaining: c.remaining + amount * 0.5 }));
        toast.success(`${amount.toLocaleString()} CROSS locked`);
        return;
      }
      await capacityAction('lock', amount, owner, walletClient);
      toast.success('Lock confirmed');
    } finally { setBusy(false); }
  }, [capacityAction]);

  const requestUnlock = useCallback(async (amount: number, owner?: string, walletClient?: any) => {
    if (!(amount > 0)) throw new Error('Enter a positive CROSS amount');
    if (amount > capacity.locked) throw new Error('Unlock amount exceeds active locked CROSS');
    setBusy(true);
    try {
      if (isPreview()) {
        setCapacity(c => ({ ...c, locked: c.locked - amount, exitAmount: c.exitAmount + amount, exitReadyAt: new Date(Date.now() + 7 * 864e5).toISOString() }));
        toast.success('Unlock requested; capacity removed immediately.');
        return;
      }
      await capacityAction('requestUnlock', amount, owner, walletClient);
      toast.success('Unlock request confirmed');
    } finally { setBusy(false); }
  }, [capacity.locked, capacityAction]);

  const cancelUnlock = useCallback(async (owner?: string, walletClient?: any) => {
    setBusy(true);
    try {
      if (isPreview()) {
        setCapacity(c => ({ ...c, locked: c.locked + c.exitAmount, exitAmount: 0, exitReadyAt: undefined }));
        toast.success('Unlock cancelled');
        return;
      }
      await capacityAction('cancelUnlock', undefined, owner, walletClient);
      toast.success('Unlock cancelled');
    } finally { setBusy(false); }
  }, [capacityAction]);

  const withdrawUnlocked = useCallback(async (owner?: string, walletClient?: any) => {
    setBusy(true);
    try {
      if (isPreview()) {
        if (!capacity.exitReadyAt || new Date(capacity.exitReadyAt).getTime() > Date.now()) throw new Error('Unlock is not mature yet');
        setCapacity(c => ({ ...c, exitAmount: 0, exitReadyAt: undefined }));
        toast.success('Matured CROSS withdrawn');
        return;
      }
      await capacityAction('withdrawUnlocked', undefined, owner, walletClient);
      toast.success('Withdrawal confirmed');
    } finally { setBusy(false); }
  }, [capacity.exitReadyAt, capacityAction]);

  const saveTemplate = useCallback((name: string) => {
    const trimmed = name.trim() || 'Custom Target';
    setTemplates(t => [...t.filter(x => x.name !== trimmed), { name: trimmed, targets: draft.targets.map(x => ({ symbol: x.symbol, weight: x.weight })) }]);
    toast.success('Target state saved');
  }, [draft.targets]);

  const applyTemplate = useCallback((name: string) => {
    const found = templates.find(t => t.name === name);
    if (!found) return;
    setTargets(found.targets);
    toast.success(`${name} applied`);
  }, [templates, setTargets]);

  const resetDraft = useCallback(() => {
    setDraft(current => {
      if (!current.holdings.length) return EMPTY_DRAFT;
      return {
        ...current,
        targets: current.holdings.filter(item => item.allocation > 0).map(holding => ({ symbol: holding.symbol, weight: holding.allocation, value: holding.value })),
      };
    });
  }, []);

  const connectPreview = useCallback(() => {
    setPreviewAddress('demo:cross-workspace');
    setDraft(DEMO_DRAFT);
    setActivity(DEMO_ACTIVITY);
    setCapacity(DEMO_CAPACITY);
    setQuote(null);
    setOrder(null);
  }, []);

  const disconnectPreview = useCallback(() => {
    setPreviewAddress(null);
    setDraft(EMPTY_DRAFT);
    setActivity([]);
    setCapacity(EMPTY_CAPACITY);
    setQuote(null);
    setOrder(null);
  }, []);

  const value = useMemo(() => ({
    draft,
    setTarget,
    setTargets,
    setControls,
    resetDraft,
    previewAddress,
    connectPreview,
    disconnectPreview,
    quote,
    order,
    activity,
    capacity,
    token,
    requestQuote,
    submitOrder,
    refreshOrder,
    cancelOrder,
    syncAccount,
    saveTemplate,
    applyTemplate,
    templates,
    lockCross,
    requestUnlock,
    cancelUnlock,
    withdrawUnlocked,
    busy,
  }), [draft, setTarget, setTargets, setControls, resetDraft, previewAddress, connectPreview, disconnectPreview, quote, order, activity, capacity, token, requestQuote, submitOrder, refreshOrder, cancelOrder, syncAccount, saveTemplate, applyTemplate, templates, lockCross, requestUnlock, cancelUnlock, withdrawUnlocked, busy]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCross() {
  const value = useContext(Ctx);
  if (!value) throw new Error('CrossProvider missing');
  return value;
}
